/**
 * priorYears (HOF-6) — the −1/−2/−3-Jahr overlays, borrowed wholesale from
 * ernteprognose's #2373 pattern.
 *
 * The chart's axis is one fixed year wide, so "how does this season compare with
 * last one" cannot be answered by scrolling any more — there is nowhere to
 * scroll to. It is answered by OVERLAYING: a prior year's observations are
 * shifted forward whole calendar years, landing on the same day of the year, and
 * drawn thin and grey under the live line.
 *
 * **The shift happens on the RAW OBSERVATIONS, before aggregation, and that
 * ordering is load-bearing.** Shifting the finished buckets instead would move a
 * week bucket off its Monday (365 is not a multiple of 7) and a month bucket off
 * the 1st in a leap year, so the overlay's points would sit between the live
 * line's points rather than on them — a comparison chart whose two series cannot
 * be compared at any x. Shifting first and bucketing after puts both on the same
 * grid by construction, at every grain.
 *
 * The offsets are years, never days, for the same reason ernteprognose's are:
 * seasonality is a calendar phenomenon. A 365-day shift would drift a day per
 * leap year and put a Whitsun peak on a Tuesday.
 */
import { shiftYears } from '../ernteprognose/priorYearOverlay';
import type { PriceSeries } from './types';

/** The offsets the overlay chips offer — the previous three years. */
export const PRIOR_YEAR_OFFSETS: readonly number[] = [1, 2, 3];

/**
 * `?py=` → the enabled offsets, ascending and de-duplicated.
 *
 * Comma-separated, exactly as ernteprognose's own `?py=1,2,3`, so one reader's
 * deep links read the same in both apps. Junk members are dropped rather than
 * failing the whole param: `?py=1,foo,2` enables 1 and 2, because losing a valid
 * offset to a neighbouring typo is a worse answer than ignoring the typo.
 */
export function resolvePriorYears(param: string | null): number[] {
  if (!param) return [];
  const wanted = new Set<number>();
  for (const raw of param.split(',')) {
    const n = Number(raw.trim());
    if (PRIOR_YEAR_OFFSETS.includes(n)) wanted.add(n);
  }
  return [...wanted].sort((a, b) => a - b);
}

/** Serialise enabled offsets back to `?py=`, or null when none are on. */
export function priorYearsParam(offsets: readonly number[]): string | null {
  const on = [...new Set(offsets)].filter((o) => PRIOR_YEAR_OFFSETS.includes(o)).sort((a, b) => a - b);
  return on.length ? on.join(',') : null;
}

/**
 * Shift every observation forward by `years`, so a prior season lands on the
 * current one's calendar.
 *
 * Series identity is preserved apart from the id, which is namespaced with the
 * offset: two offsets of the same counterparty must not collide into one series
 * when they are aggregated, and the raw id would.
 *
 * Feb 29 clamps to Feb 28 in a non-leap target year (`shiftYears`), so a shift
 * never silently rolls a February observation into March.
 */
export function shiftSeriesYears(series: readonly PriceSeries[], years: number): PriceSeries[] {
  if (years === 0) return [...series];
  return series.map((s) => ({
    ...s,
    seriesId: `py${years}__${s.seriesId}`,
    points: s.points.map((p) => ({ ...p, date: shiftYears(p.date, years) })),
  }));
}
